import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HOTKEY_MODIFIERS,
  PORTAL_PAYMENT_METHODS,
  isIpv4Address,
  parsePortalHiddenSections,
  type GeneralSettingsDto,
  type PortalPaymentMethod,
} from '@gc/shared';

import {
  Field,
  FieldGroup,
  MediaUpload,
  PageHeading,
  SaveBar,
  Section,
  Toggle,
  ghostPillClass,
  inputClass,
} from './controls';
import { KioskUpdateSection } from './KioskUpdateSection';
import { PortalSectionsCard } from './PortalSectionsCard';
import { portalSectionsError } from './portalSections';
import { useGeneralForm, type GeneralSettingsPatch } from './useGeneralForm';
import { useUnsavedGuard } from './useUnsavedGuard';
import { useResource } from './useSettings';

/**
 * The slice of `GET /screens` this page reads for the hero-video picker
 * (wave 3, #3). The Screens area owns uploads and the canonical DTOs; the
 * picker only needs id, name and a signed URL for the preview.
 */
interface HeroVideoLibrary {
  media: { id: string; fileName: string; url: string }[];
}

/**
 * The fields THIS page writes. One function for Confirm and for the
 * unsaved-changes guard — see VenuePage for why they must not be written twice.
 *
 * `hotkeyPassword` is the exception that proves the rule: it is not part of
 * the row, so it cannot be compared against the row. Typing into it is dirty
 * on its own (below), and it is omitted here whenever it is blank.
 */
export function portalPayload(
  row: GeneralSettingsDto,
  hotkeyPassword: string,
): GeneralSettingsPatch {
  return {
    enablePortalAccountCreation: row.enablePortalAccountCreation,
    // The WHOLE list, every save: the field replaces what is stored, and an
    // empty list means "show everything", not "leave it alone". Read through
    // the same shared parser the card's ticks come from, so what Confirm sends
    // is exactly what the card showed.
    portalHiddenSections: parsePortalHiddenSections(row.portalHiddenSections),
    // No `platformAccountMode` since the borrow system went (owner,
    // 2026-10-05): the patch schema reads an absent key as "leave it", so the
    // stored value stays put, unread, and a rollback still finds it.
    enablePortalOrders: row.enablePortalOrders,
    portalPaymentMethod: row.portalPaymentMethod,
    enableCardLogin: row.enableCardLogin,
    cardLoginHoldSeconds: row.cardLoginHoldSeconds,
    heroMediaAssetId: row.heroMediaAssetId,
    hotkeyCombo: row.hotkeyCombo,
    // Omitted entirely when untouched, so saving the page does not re-hash
    // (and therefore invalidate) a password the owner did not change.
    ...(hotkeyPassword.trim() ? { hotkeyPassword } : {}),
    enableHotkeyPassword: row.enableHotkeyPassword,
    hotkeyAutoRelockMinutes: row.hotkeyAutoRelockMinutes,
    signOutHotkeyCombo: row.signOutHotkeyCombo,
    // Trimmed, and blank as null: a stray space must neither fail the IPv4
    // check nor be stored as an address.
    centralDeviceIp: row.centralDeviceIp?.trim() || null,
  };
}

/**
 * Portal & kiosk — what a customer can do on a gaming PC, and the staff
 * hot-keys that take it back (SPEC §6.1).
 *
 * The one field here that is NOT a plain column write is the hot-key password:
 * it is write-only (Argon2id-hashed server-side, never read back), so a blank
 * box means "leave the stored hash alone" and is omitted from the payload
 * entirely. Sending it every time would re-hash — and therefore invalidate —
 * a password nobody changed, which is exactly what saving this page must never
 * do to a venue whose staff are mid-shift.
 */
export function PortalPage() {
  const { t } = useTranslation();
  const {
    data,
    form,
    set,
    setForm,
    loading,
    error,
    errors,
    errorFor,
    setErrors,
    saving,
    saved,
    save,
  } = useGeneralForm();

  // May 403 for staff without media.manage — the picker degrades to a notice.
  const videoLibrary = useResource<HeroVideoLibrary>('/screens');

  /** Blank means "leave the stored hash alone" (SPEC §6.1). */
  const [hotkeyPassword, setHotkeyPassword] = useState('');

  /** Videos the hero picker can choose from — grows in place after an upload. */
  const heroLibrary = videoLibrary.data?.media ?? [];

  // Dirty when Confirm would send something the server does not already hold,
  // or when a new hot-key password is sitting in the box unsaved.
  useUnsavedGuard(
    (!!form &&
      !!data &&
      JSON.stringify(portalPayload(form, '')) !== JSON.stringify(portalPayload(data, ''))) ||
      hotkeyPassword.trim().length > 0,
  );

  if (loading && !form)
    return <p className="p-6 text-sm text-content-muted">{t('common.loading')}</p>;
  if (error) return <p className="p-6 text-sm text-danger">{error}</p>;
  if (!form) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form) return;

    const payload = portalPayload(form, hotkeyPassword);
    // The shared rule the server's schema holds (`isIpv4Address`), checked
    // here too so the owner hears about a typo at the field, in their own
    // language, before the save goes out.
    if (payload.centralDeviceIp && !isIpv4Address(payload.centralDeviceIp)) {
      setErrors([
        { field: 'centralDeviceIp', message: t('settings.general.centralDeviceIpInvalid') },
      ]);
      return;
    }

    // Only once the row came back changed: clearing the box on a REFUSED save
    // would throw away a password that was never stored.
    if (await save(payload, t('settings.general.saveFailed'))) {
      setHotkeyPassword('');
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="p-6">
      <PageHeading
        title={t('settings.general.portalTitle')}
        description={t('settings.general.portalDescription')}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          {/*
            First on the page because it is the broadest thing on it: whole
            areas of the portal, not a behaviour inside one (owner,
            2026-09-28). Saved with the rest of the page by Confirm; a PC in
            use picks the change up on its next session poll.
          */}
          <PortalSectionsCard
            id="set-portal-sections"
            hidden={form.portalHiddenSections}
            onChange={(next) => set('portalHiddenSections', next)}
            error={portalSectionsError(errors, (key) => t(key))}
          />

          <Section id="set-portal-accounts" title={t('settings.general.portalAccountsSection')}>
            <Toggle
              label={t('settings.general.enablePortalAccountCreation')}
              checked={form.enablePortalAccountCreation}
              onChange={(value) => set('enablePortalAccountCreation', value)}
            />

            {/*
              "Platform accounts in the portal" lived here until the borrow
              system went (owner, 2026-10-05): with no account picker there
              is nothing to suggest. Each PC now plays on the accounts staff
              keep signed in on it (Settings › Game accounts, Made for PC).
            */}
          </Section>

          <Section id="set-portal-orders" title={t('settings.general.portalOrdersSection')}>
            <Toggle
              label={t('settings.general.enableOrderFromPortal')}
              checked={form.enablePortalOrders}
              onChange={(value) => set('enablePortalOrders', value)}
            />

            {/*
              The three words alone do not say who pays where, and the rule
              the server enforces (`portalOrderPayment`) differs sharply
              between them: whether a walk-in can order from a PC at all, and
              whether an account's order waits on the bill. The hint under
              the picker spells out the chosen one (2026-09-24).
            */}
            <Field
              label={t('settings.general.portalPaymentMethods')}
              hint={t(`settings.general.portalPaymentHint.${form.portalPaymentMethod}`)}
            >
              <select
                value={form.portalPaymentMethod}
                onChange={(event) =>
                  set('portalPaymentMethod', event.target.value as PortalPaymentMethod)
                }
                className={inputClass}
              >
                {PORTAL_PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {t(`settings.general.portalPayment.${method}`)}
                  </option>
                ))}
              </select>
            </Field>
          </Section>

          <Section id="set-kiosk-screen" title={t('settings.general.kioskScreenSection')}>
            {/* ---- Wave 3 (#3): kiosk hero video --------------------------- */}
            {/*
              The upload lives INSIDE this field on purpose. It used to live
              only on the Screens page, while the thing that consumes it is
              this dropdown — so the owner uploaded a background video, never
              came here, and never saw it play. Uploading alone changed
              nothing, and nothing on either screen said so. Now one action
              does both: the upload lands in the library AND is selected here.
            */}
            <FieldGroup
              label={t('settings.general.heroVideo')}
              hint={t('settings.general.heroVideoHint')}
              error={errorFor('heroMediaAssetId')}
            >
              {videoLibrary.error ? (
                <>
                  <p className="text-sm text-content-muted">
                    {t('settings.general.heroVideoUnavailable')}
                  </p>
                  {/* The notice promises this preview; without media.manage
                      there is no upload control to carry it. */}
                  {form.heroVideoUrl ? (
                    <video
                      src={form.heroVideoUrl}
                      muted
                      controls
                      playsInline
                      className="mt-2 aspect-video w-full max-w-xs rounded-control border border-subtle bg-black object-contain"
                    />
                  ) : null}
                </>
              ) : heroLibrary.length === 0 ? (
                // A real empty state, not an empty dropdown: a select with one
                // "No hero video" row and nothing else reads as broken, and
                // gives no clue where videos come from.
                <p data-hero-empty="" className="text-sm text-content-muted">
                  {t('settings.general.heroVideoEmpty')}
                </p>
              ) : (
                <select
                  value={form.heroMediaAssetId ?? ''}
                  onChange={(event) => {
                    const assetId = event.target.value || null;
                    const asset = heroLibrary.find((entry) => entry.id === assetId);
                    setForm((current) =>
                      current
                        ? {
                            ...current,
                            heroMediaAssetId: assetId,
                            // The signed URL from the library previews below
                            // immediately; the server re-signs on save anyway.
                            heroVideoUrl: asset?.url ?? null,
                          }
                        : current,
                    );
                  }}
                  className={inputClass}
                >
                  <option value="">{t('settings.general.heroVideoNone')}</option>
                  {heroLibrary.map((asset) => (
                    <option key={asset.id} value={asset.id}>
                      {asset.fileName}
                    </option>
                  ))}
                </select>
              )}

              {videoLibrary.error ? null : (
                <div className="mt-2">
                  <MediaUpload
                    label={t('settings.general.heroVideoUpload')}
                    kind="IDLE_VIDEO"
                    currentUrl={form.heroVideoUrl}
                    previewClassName="aspect-video w-full max-w-xs"
                    onUploaded={(assetId, url, asset) => {
                      // Into the library AND into the field, in one move.
                      videoLibrary.setData((current) => ({
                        media: [
                          { id: asset.id, fileName: asset.fileName, url: asset.url },
                          ...(current?.media ?? []).filter((entry) => entry.id !== asset.id),
                        ],
                      }));
                      setForm((current) =>
                        current
                          ? { ...current, heroMediaAssetId: assetId, heroVideoUrl: url }
                          : current,
                      );
                    }}
                    // The trash icon deselects — the same thing the dropdown's
                    // "No hero video" row does. It does not delete the file;
                    // the library is the Screens page's to manage.
                    onCleared={() =>
                      setForm((current) =>
                        current
                          ? { ...current, heroMediaAssetId: null, heroVideoUrl: null }
                          : current,
                      )
                    }
                  />
                </div>
              )}
            </FieldGroup>
          </Section>
        </div>

        <div className="space-y-4">
          <Section id="set-kiosk-login" title={t('settings.general.kioskLoginSection')}>
            <Toggle
              label={t('settings.general.enableLoginWithCard')}
              checked={form.enableCardLogin}
              onChange={(value) => set('enableCardLogin', value)}
            />

            <Field
              label={t('settings.general.loginHoldTime')}
              hint={t('settings.general.loginHoldTimeHint')}
              error={errorFor('cardLoginHoldSeconds')}
            >
              <input
                type="number"
                min="1"
                max="30"
                value={form.cardLoginHoldSeconds}
                onChange={(event) => set('cardLoginHoldSeconds', Number(event.target.value) || 1)}
                className={`${inputClass} tabular`}
              />
            </Field>
          </Section>

          <Section id="set-hotkeys" title={t('settings.general.accessSection')}>
            <HotkeyPicker
              value={form.hotkeyCombo}
              onChange={(combo) => set('hotkeyCombo', combo)}
              error={errorFor('hotkeyCombo')}
            />

            {/* ---- Wave 3 (#7): the sign-out combo ------------------------- */}
            {form.signOutHotkeyCombo === null ? (
              <Field
                label={t('settings.general.signOutHotKey')}
                hint={t('settings.general.signOutHotKeyHint')}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm text-content-muted">
                    {t('settings.general.signOutHotKeyOff')}
                  </span>
                  <button
                    type="button"
                    onClick={() => set('signOutHotkeyCombo', 'Control+Shift+Q')}
                    className={ghostPillClass}
                  >
                    {t('settings.general.signOutHotKeyEnable')}
                  </button>
                </div>
              </Field>
            ) : (
              <div>
                <HotkeyPicker
                  label={t('settings.general.signOutHotKey')}
                  hint={t('settings.general.signOutHotKeyHint')}
                  value={form.signOutHotkeyCombo}
                  onChange={(combo) => set('signOutHotkeyCombo', combo)}
                  error={errorFor('signOutHotkeyCombo')}
                />
                <button
                  type="button"
                  onClick={() => set('signOutHotkeyCombo', null)}
                  className="mt-1 text-xs text-content-secondary underline hover:text-danger"
                >
                  {t('settings.general.signOutHotKeyDisable')}
                </button>
              </div>
            )}

            <Field
              label={t('settings.general.hotKeyPassword')}
              hint={
                form.hotkeyPasswordSet
                  ? t('settings.general.hotKeyPasswordSet')
                  : t('settings.general.hotKeyPasswordUnset')
              }
              error={errorFor('hotkeyPassword')}
            >
              <input
                type="password"
                value={hotkeyPassword}
                autoComplete="new-password"
                placeholder={
                  form.hotkeyPasswordSet ? t('settings.general.leaveBlankToKeep') : undefined
                }
                onChange={(event) => setHotkeyPassword(event.target.value)}
                className={inputClass}
              />
            </Field>

            <Toggle
              label={t('settings.general.enablePassword')}
              checked={form.enableHotkeyPassword}
              onChange={(value) => set('enableHotkeyPassword', value)}
            />

            {/*
              The relock delay was already honoured by every agent and by the
              realtime frames — it simply had no control, so changing it meant
              a database edit. It belongs beside the password it re-asks for.
            */}
            <Field
              label={t('settings.general.autoRelock')}
              hint={t('settings.general.autoRelockHint')}
              error={errorFor('hotkeyAutoRelockMinutes')}
            >
              <input
                type="number"
                min="1"
                max="240"
                value={form.hotkeyAutoRelockMinutes}
                onChange={(event) =>
                  set('hotkeyAutoRelockMinutes', Number(event.target.value) || 1)
                }
                className={`${inputClass} tabular`}
              />
            </Field>
          </Section>

          {/*
            §6.1 "Central Device IP", which is what the search still finds it
            by: the MAIN-PC's address on the venue LAN. The PCs copy their idle
            video from it before the internet (2026-09-24).
          */}
          <Section id="set-central-device" title={t('settings.general.centralDeviceSection')}>
            <Field
              label={t('settings.general.centralDeviceIp')}
              hint={t('settings.general.centralDeviceIpHint')}
              error={errorFor('centralDeviceIp')}
            >
              <input
                value={form.centralDeviceIp ?? ''}
                onChange={(event) => set('centralDeviceIp', event.target.value || null)}
                placeholder="192.168.1.150"
                inputMode="decimal"
                autoComplete="off"
                spellCheck={false}
                data-central-device-ip
                className={`${inputClass} tabular`}
              />
            </Field>
          </Section>

          {/* ---- Wave 5: hurry the kiosk app along ------------------------- */}
          {/* Its own immediate actions, not part of this form's Confirm. */}
          <KioskUpdateSection id="set-kiosk-update" />
        </div>
      </div>

      <SaveBar saving={saving} saved={saved} error={errorFor('(root)')} />
    </form>
  );
}

/**
 * Hot-key combo picker (SPEC §6.1, e.g. Control+Shift+I).
 *
 * Modifiers are checkboxes and the final key is a single character, rather than
 * a free-text field: the agent matches this string exactly, so "Ctrl + Shift+i"
 * typed by hand would silently never fire.
 */
function HotkeyPicker({
  value,
  onChange,
  error,
  label,
  hint,
}: {
  value: string;
  onChange: (combo: string) => void;
  error?: string;
  label?: string;
  hint?: string;
}) {
  const { t } = useTranslation();

  const parts = value.split('+').map((part) => part.trim());
  const key = parts.at(-1) ?? '';
  const active = new Set(parts.slice(0, -1));

  const rebuild = (modifiers: Set<string>, finalKey: string) =>
    onChange([...HOTKEY_MODIFIERS.filter((m) => modifiers.has(m)), finalKey].join('+'));

  return (
    <Field
      label={label ?? t('settings.general.hotKey')}
      hint={hint ?? t('settings.general.hotKeyHint')}
      error={error}
    >
      <div className="flex flex-wrap items-center gap-2">
        {HOTKEY_MODIFIERS.map((modifier) => (
          <label
            key={modifier}
            className="flex items-center gap-1.5 rounded-control border border-subtle bg-input px-2.5 py-1.5"
          >
            <input
              type="checkbox"
              checked={active.has(modifier)}
              onChange={(event) => {
                const next = new Set(active);
                if (event.target.checked) next.add(modifier);
                else next.delete(modifier);
                rebuild(next, key);
              }}
              className="h-4 w-4 accent-[var(--brand-primary)]"
            />
            <span className="text-sm text-content">{modifier}</span>
          </label>
        ))}

        <input
          value={key}
          maxLength={12}
          aria-label={t('settings.general.hotKeyFinalKey')}
          onChange={(event) => rebuild(active, event.target.value.toUpperCase())}
          className={`${inputClass} w-20 text-center`}
        />
      </div>
      <p className="mt-1 text-xs text-content-muted">
        {t('settings.general.hotKeyCurrent')}: <code className="text-content">{value}</code>
      </p>
    </Field>
  );
}
